/*
 * Terns UV/weather widget — hydrates the header status line (city · UV · temp)
 * with live data. Hits the Cloudflare Worker directly (cross-origin), caches the
 * payload in localStorage for ~1h. Geolocation is done server-side from the
 * visitor's connection (Cloudflare request.cf) — no Shopify App / proxy, no
 * geolocation prompt.
 *
 * Backend: ../uv-weather-service (Cloudflare Worker). Response shape:
 *   { ok, visitor: { city, region, uv, uvLevel, temp, tempUnit, condition }|null,
 *     cities: [...], citiesUpdatedAt }
 *
 * Fail-quiet: the status line starts hidden and is only revealed when a visitor
 * is resolved. Any failure (network error, non-200, unresolved location) leaves
 * it hidden — the component simply doesn't show. The header re-measures its
 * 3-state layout once the line appears (dispatched 'terns:uvstatus').
 */
(function () {
  'use strict';

  // Always the live Worker — even under `shopify theme dev` (127.0.0.1:9292).
  // The Worker's CORS allowlist always permits localhost origins, so there's no
  // local Worker to run; geolocation comes from the visitor's connection.
  var ENDPOINT = 'https://uv-weather-service.josh-4de.workers.dev/';
  var CACHE_KEY = 'terns:uv:v1';
  var CACHE_TTL_MS = 60 * 60 * 1000; // 1 hour

  function readCache() {
    try {
      var raw = localStorage.getItem(CACHE_KEY);
      if (!raw) return null;
      var parsed = JSON.parse(raw);
      if (!parsed || typeof parsed.t !== 'number') return null;
      if (Date.now() - parsed.t > CACHE_TTL_MS) return null;
      return parsed.data;
    } catch (e) {
      return null;
    }
  }

  function writeCache(data) {
    try {
      localStorage.setItem(CACHE_KEY, JSON.stringify({ t: Date.now(), data: data }));
    } catch (e) {
      /* storage full / disabled — non-fatal */
    }
  }

  function fetchData() {
    return fetch(ENDPOINT, { mode: 'cors', credentials: 'omit', headers: { Accept: 'application/json' } })
      .then(function (res) {
        if (!res.ok) throw new Error('uv endpoint ' + res.status);
        return res.json();
      })
      .then(function (data) {
        if (data && data.ok) writeCache(data);
        return data;
      });
  }

  // Reveal (or hide) the header status line(s). Mirrors the hardcoded format the
  // CSS expects ("LOS ANGELES. UV INDEX 6, 72°" — uppercased via text-transform).
  function hydrateHeader(visitor) {
    var els = document.querySelectorAll('[data-uv-status]');
    if (!els.length) return;
    var changed = false;
    for (var i = 0; i < els.length; i++) {
      var el = els[i];
      if (visitor && visitor.city) {
        el.textContent =
          visitor.city + '. UV Index ' + Math.round(visitor.uv) + ', ' + visitor.temp + '°';
        if (el.hidden) changed = true;
        el.hidden = false;
      } else {
        // Fail quiet: hide rather than show a wrong/placeholder location.
        el.hidden = true;
      }
    }
    // The header measures its layout off the visible status text — tell it to
    // re-measure now that the line has appeared (or changed width).
    if (changed) document.dispatchEvent(new CustomEvent('terns:uvstatus'));
  }

  function hydrate(data) {
    hydrateHeader(data && data.ok ? data.visitor : null);
  }

  function init() {
    if (!document.querySelector('[data-uv-status]')) return;

    var cached = readCache();
    if (cached) {
      hydrate(cached);
      return;
    }
    fetchData()
      .then(hydrate)
      .catch(function () {
        hydrateHeader(null); // network/endpoint failure -> stay hidden
      });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
